<template>
  <a-card
      class="management-additional-analysis-card"
      :body-style="cardBodyStyle"
  >
    <header class="management-additional-analysis-card__header">
      <h3 class="management-additional-analysis-card__title">
        {{ title }}
      </h3>

      <span class="management-additional-analysis-card__average">
        {{ displayAverage }}
      </span>
    </header>

    <div
        v-if="normalizedLevels.length"
        class="management-additional-analysis-card__legend"
    >
      <div
          v-for="item in normalizedLevels"
          :key="item.key"
          class="management-additional-analysis-card__legend-row"
      >
        <div class="management-additional-analysis-card__legend-name">
          <span
              class="management-additional-analysis-card__legend-mark"
              :style="{ backgroundColor: item.legendColor }"
          ></span>

          <span>{{ item.label }}</span>
        </div>

        <span class="management-additional-analysis-card__legend-value">
          {{ item.value }}
        </span>
      </div>
    </div>

    <div
        v-if="reversedBarSegments.length"
        class="management-additional-analysis-card__bar-block"
    >
      <div
          class="management-additional-analysis-card__bar"
          :style="reversedBarGridStyle"
      >
        <div
            v-for="(item, index) in reversedBarSegments"
            :key="item.key"
            class="management-additional-analysis-card__bar-part"
            :style="getBarPartStyle(index)"
        ></div>
      </div>

      <div
          class="management-additional-analysis-card__bar-values"
          :style="reversedBarGridStyle"
      >
        <span
            v-for="item in reversedBarSegments"
            :key="item.key"
            class="management-additional-analysis-card__bar-value"
        >
          {{ item.value }}
        </span>
      </div>
    </div>
  </a-card>
</template>

<script>
import { computed } from 'vue'
import { Card } from 'ant-design-vue'

const GREY_COLOR = '#D1D1D1'

const cardBodyStyle = {
  padding: '20px',
  minHeight: '188px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}

export default {
  name: 'ManagementAdditionalAnalysisCard',

  components: {
    ACard: Card,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    averageValue: {
      type: [Number, String, null],
      default: null,
    },
    levels: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const toNumber = (value) => {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    }

    const displayAverage = computed(() => {
      if (
          props.averageValue === null ||
          props.averageValue === undefined ||
          props.averageValue === ''
      ) {
        return '-'
      }

      return Number(props.averageValue);
    })

    const normalizedLevels = computed(() => {
      return props.levels.map((item) => {
        const value = toNumber(item.value)
        const baseColor = item.color || GREY_COLOR

        return {
          ...item,
          value,
          legendColor: baseColor,
          color: value === 0 ? GREY_COLOR : baseColor,
        }
      })
    })

    const levelsTotal = computed(() => {
      return normalizedLevels.value.reduce((sum, item) => sum + item.value, 0)
    })

    const allLevelsAreZero = computed(() => {
      return (
          normalizedLevels.value.length > 0 &&
          normalizedLevels.value.every((item) => item.value === 0)
      )
    })

    const barSegments = computed(() => {
      if (!normalizedLevels.value.length) {
        return []
      }

      if (allLevelsAreZero.value) {
        return normalizedLevels.value.map((item) => ({
          ...item,
          color: GREY_COLOR,
          ratio: 1,
        }))
      }

      if (!levelsTotal.value) {
        return []
      }

      return normalizedLevels.value
          .filter((item) => item.value > 0)
          .map((item) => ({
            ...item,
            ratio: item.value,
          }))
    })

    const reversedBarSegments = computed(() => {
      return [...barSegments.value].reverse()
    })

    const reversedBarGridStyle = computed(() => {
      if (!reversedBarSegments.value.length) {
        return {}
      }

      return {
        gridTemplateColumns: reversedBarSegments.value
            .map((item) => `${item.ratio}fr`)
            .join(' '),
      }
    })

    const getBarPartStyle = (index) => {
      const isFirst = index === 0
      const isLast = index === reversedBarSegments.value.length - 1
      const currentItem = reversedBarSegments.value[index]

      return {
        backgroundColor: currentItem.color,
        borderTopLeftRadius: isFirst ? '999px' : '0',
        borderBottomLeftRadius: isFirst ? '999px' : '0',
        borderTopRightRadius: isLast ? '999px' : '0',
        borderBottomRightRadius: isLast ? '999px' : '0',
      }
    }

    return {
      cardBodyStyle,
      displayAverage,
      normalizedLevels,
      reversedBarSegments,
      reversedBarGridStyle,
      getBarPartStyle,
    }
  },
}
</script>

<style scoped lang="scss">
.management-additional-analysis-card {
  width: 100%;
  height: 100%;
}

.management-additional-analysis-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

}

.management-additional-analysis-card__title {
  margin: 0;
  color: rgba(0, 0, 0, 0.88);
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
}

.management-additional-analysis-card__average {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.88);
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
}

.management-additional-analysis-card__legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.management-additional-analysis-card__legend-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.management-additional-analysis-card__legend-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.management-additional-analysis-card__legend-name span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.management-additional-analysis-card__legend-mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.management-additional-analysis-card__legend-value {
  color: rgba(0, 0, 0, 0.88);
  white-space: nowrap;
}

.management-additional-analysis-card__bar-block {
  margin-top: auto;
}

.management-additional-analysis-card__bar {
  display: grid;
  width: 100%;
  height: 8px;
  margin-top: 16px;
  gap: 2px;
}

.management-additional-analysis-card__bar-part {
  height: 100%;
  min-width: 0;
}

.management-additional-analysis-card__bar-values {
  display: grid;
  width: 100%;
  margin-top: 6px;
  gap: 2px;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.management-additional-analysis-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
